import {SerializedStyles, CSSObject} from "@emotion/react";
import {Theme} from "../theme";
import {getTheme, StyleFn} from "../utils";

type ValueOf<T> = T[keyof T];

export type FormProps = {
  /**
   * If `true`, use crossBrand style.
   */
  crossBrand?: boolean;
  /**
   * If `true`, will render in a "disabled" state
   */
  disabled?: boolean;
  /**
   * If `true`, will render in a "focused" state
   */
  focus?: boolean;
  /**
   * If `true`, and `errorMessage` exists, will render in an "error" state
   */
  hasError?: boolean;
  /**
   * If `true`, use inverse (dark background) color scheme
   */
  inverse?: boolean;
  /**
   * If `true`, indicates the form element has a value, has an error, and/or is focused
   */
  isExpanded?: boolean;
  /**
   * If true, the checkbox will be checked.
   */
  isChecked?: boolean;
};

/**
 * The shape of style helper functions for forms,
 * @see StyleFn
 */
export type FormStyleFn<ReturnType = SerializedStyles> = StyleFn<
  FormProps,
  ReturnType
>;

type BaseColorKey = keyof Theme["color"];

export type BaseColor = ValueOf<Theme["color"]>;

/**
 * Font size helper,
 * @returns The standard font sizes for form components per brand,
 */
export const getFontSize: FormStyleFn<string> = ({theme, crossBrand}) => {
  if (crossBrand || ["on", "br", "brfs"].includes(theme.brand)) return "1rem";
  return "1.0625rem";
};

/**
 * Returns the provided color or one of the component's state-dependent colors,
 * @param baseColorKey: key to the color to return if no state-dependend colors are returned,
 * @returns FormStyleFn that returns the correct color,
 */
export const getBaseColor =
  (baseColorKey: BaseColorKey): FormStyleFn<BaseColor> =>
  ({theme, inverse, disabled}): BaseColor => {
    const {color} = theme;
    if (inverse && disabled) return color.whAlpha50;
    if (inverse) return color.wh;
    if (disabled) return color.gray20;
    return color[baseColorKey];
  };

/**
 * Returns the provided color or one of the component's state-dependent colors, including focus,
 * @see getBaseColor,
 */
export const getFocusableColor =
  (baseColorKey: BaseColorKey): FormStyleFn<BaseColor> =>
  ({theme, crossBrand, inverse, disabled, hasError, focus}): BaseColor => {
    if (inverse && disabled) return theme.color.whAlpha50;
    if (inverse) return theme.color.wh;
    if (disabled) return theme.color.gray20;
    if (hasError) return getTheme({theme, crossBrand}).color.err1;
    if (focus) {
      if (crossBrand || ["gap", "gapfs", "on"].includes(theme.brand))
        return getTheme({theme, crossBrand}).color.b1;
      return theme.color.g1;
    }
    return theme.color[baseColorKey];
  };

/**
 * Returns expanded or not expanded styles depending on component's state. Used for the Label and Hint components,
 */
export const animatedFontStyle: FormStyleFn<CSSObject> = ({
  theme,
  crossBrand,
  isExpanded,
}) => {
  if (isExpanded)
    return {
      fontSize:
        !crossBrand && ["gap", "gapfs", "at"].includes(theme.brand)
          ? "0.6875rem"
          : "0.75rem",
      textTransform: "uppercase",
    };
  return {
    fontSize: getFontSize({theme, crossBrand}),
    textTransform: "inherit",
  };
};

/**
 * Removes mouse interaction styles for disabled elements,
 */
export const disabledTextStyle: FormStyleFn<CSSObject | undefined | boolean> =
  ({disabled}) => disabled && {pointerEvents: "none", cursor: "default"};
